import React, { useState, useEffect } from "react";
import TransactionApiClient from "../../services/TransactionApiClient";
import AccountApiClient from "../../services/AccountApiClient";
import TransactionPreview from "./TransactionPreview";
import { Account, Transaction, Terminal, TransactionFormData, TransactionDisplayValues, ValidationErrors } from "../../types";

interface AddTransactionProps {
  isOpen: boolean;
  businessId: string;
  transaction?: Transaction | null;
  onClose: () => void;
  onSave: () => void;
}

const AddTransaction: React.FC<AddTransactionProps> = ({
  isOpen,
  businessId,
  transaction,
  onClose,
  onSave,
}) => {
  const generateOrderId = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const year = String(now.getFullYear()).slice(-2);
    return `ORD-${month}${day}${year}-0`;
  };

  // Currency formatting utilities
  const formatCurrency = (value: number): string => {
    return `$${value.toFixed(2)}`;
  };

  const parseCurrencyToNumber = (value: string): number => {
    const cleanValue = value.replace(/[^\d]/g, "");
    if (cleanValue === "") return 0;
    return parseFloat(cleanValue) / 100;
  };

  const formatCurrencyInput = (input: string): string => {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");
    if (digits === "") return "$0.00";
    
    // Convert to cents and format
    const cents = parseInt(digits, 10);
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  };

  const [formData, setFormData] = useState<TransactionFormData>({
    orderId: generateOrderId(),
    amount: 0,
    tipAmount: 0,
    accountId: "",
    terminalId: "",
  });
  
  // Display values for currency formatting
  const [displayValues, setDisplayValues] = useState<TransactionDisplayValues>({
    amount: "$0.00",
    tipAmount: "$0.00",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [availableTerminals, setAvailableTerminals] = useState<Terminal[]>([]);
  const [shouldShowPanel, setShouldShowPanel] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Handle slide animation
  useEffect(() => {
    if (isOpen) {
      // Add the open class on the next tick to trigger the transition
      const timer = setTimeout(() => {
        setShouldShowPanel(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setShouldShowPanel(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && businessId) {
      fetchAccounts();
    }
  }, [isOpen, businessId]);

  // Update available terminals when account selection changes
  useEffect(() => {
    if (formData.accountId) {
      const selectedAccount = accounts.find(
        (account) => account.id === formData.accountId
      );
      if (selectedAccount && selectedAccount.terminals) {
        setAvailableTerminals(selectedAccount.terminals);
      } else {
        setAvailableTerminals([]);
      }
    } else {
      setAvailableTerminals([]);
      // Clear terminal selection when no account is selected
      if (formData.terminalId) {
        setFormData((prev) => ({
          ...prev,
          terminalId: "",
        }));
      }
    }
  }, [formData.accountId, accounts]);

  useEffect(() => {
    if (isOpen) {
      if (transaction) {
        // Editing existing transaction
        const amount = transaction.amount || 0;
        const tipAmount = transaction.tip || 0;
        
        setFormData({
          orderId: transaction.invoice || "",
          amount: amount,
          tipAmount: tipAmount,
          accountId: "", // Will be set when accounts are loaded
          terminalId: "", // Will be set when accounts are loaded
        });
        
        setDisplayValues({
          amount: formatCurrency(amount),
          tipAmount: formatCurrency(tipAmount),
        });
      } else {
        // Adding new transaction
        setFormData({
          orderId: generateOrderId(),
          amount: 0,
          tipAmount: 0,
          accountId: "",
          terminalId: "",
        });
        
        setDisplayValues({
          amount: "$0.00",
          tipAmount: "$0.00",
        });
      }
      setShowPreview(false); // Always start with form view when opening modal
      setError("");
      setSuccess("");
      setValidationErrors({});
    }
  }, [isOpen, transaction]);

  const fetchAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const accountData = await AccountApiClient.fetchAccounts(businessId);
      setAccounts(accountData);

      // Set the first account as default if available and no account is currently selected
      if (accountData.length > 0 && !formData.accountId) {
        setFormData((prev) => ({
          ...prev,
          accountId: accountData[0].id,
        }));
      }
    } catch (error: any) {
      console.error("Error fetching accounts:", error);
      setError("Failed to load accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    // Order ID validation
    if (!formData.orderId.trim()) {
      errors.orderId = "Order ID is required";
    } else if (formData.orderId.length < 3) {
      errors.orderId = "Order ID must be at least 3 characters";
    }

    // Amount validation
    if (!formData.amount || formData.amount <= 0) {
      errors.amount = "Amount must be greater than 0";
    } else if (formData.amount > 9999999) {
      errors.amount = "Amount cannot exceed 9,999,999";
    }

    // Tip amount validation
    if (formData.tipAmount < 0) {
      errors.tipAmount = "Tip amount cannot be negative";
    } else if (formData.tipAmount > 9999999) {
      errors.tipAmount = "Tip amount cannot exceed 9,999,999";
    }

    // Account validation
    if (!formData.accountId) {
      errors.accountId = "Account is required";
    }

    // Terminal validation
    if (!formData.terminalId) {
      errors.terminalId = "Terminal is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    let processedValue: any = value;

    // Handle numeric fields
    if (type === "number") {
      processedValue = parseFloat(value) || 0;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCurrencyChange = (fieldName: "amount" | "tipAmount") => 
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const formattedValue = formatCurrencyInput(inputValue);
      const numericValue = parseCurrencyToNumber(formattedValue);

      // Update display value
      setDisplayValues((prev) => ({
        ...prev,
        [fieldName]: formattedValue,
      }));

      // Update form data with numeric value
      setFormData((prev) => ({
        ...prev,
        [fieldName]: numericValue,
      }));

      // Clear validation error for this field
      if (validationErrors[fieldName]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    };

  const handlePreview = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before showing preview
    if (!validateForm()) {
      setError("Please fix the validation errors before previewing");
      return;
    }

    setError("");
    setShowPreview(true);
  };

  const handleConfirmTransaction = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const transactionData = {
        orderId: formData.orderId,
        amount: formData.amount,
        tipAmount: formData.tipAmount,
        requestOrigin: "web",
      };

      const response = await TransactionApiClient.addTransaction(
        formData.accountId,
        formData.terminalId,
        transactionData
      );

      // Show success message
      setSuccess("Transaction created successfully!");

      // Close the modal after a short delay to show the success message
      setTimeout(() => {
        onSave();
      }, 1500);
    } catch (error: any) {
      console.error("Error adding transaction:", error);

      // Check if there's a specific error message from the API
      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.message ||
        "Failed to add transaction";

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForm = () => {
    setShowPreview(false);
    setError("");
  };

  const handleClose = () => {
    setFormData({
      orderId: generateOrderId(),
      amount: 0,
      tipAmount: 0,
      accountId: "",
      terminalId: "",
    });
    setDisplayValues({
      amount: "$0.00",
      tipAmount: "$0.00",
    });
    setShowPreview(false);
    setError("");
    setSuccess("");
    setValidationErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="slide-overlay" onClick={handleClose}></div>}

      {/* Slide Panel */}
      {isOpen && (
        <div
          className={`slide-panel ${shouldShowPanel ? "slide-panel-open" : ""}`}
        >
        <div className="slide-panel-header">
          <h2>{transaction ? "✏️ Edit Transaction" : "➕ Add Transaction"}</h2>
          <button className="slide-panel-close" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="slide-panel-content">
          {success && (
            <div className="success-message" style={{ margin: "0 0 1rem 0" }}>
              ✅ {success}
            </div>
          )}

          {!showPreview ? (
            <form onSubmit={handlePreview}>
              <div className="settings-grid">
                <div className="setting-item">
                  <label htmlFor="accountId">Account *</label>
                  <select
                    id="accountId"
                    name="accountId"
                    value={formData.accountId}
                    onChange={handleInputChange}
                    className={`form-input ${
                      validationErrors.accountId ? "form-input-error" : ""
                    }`}
                    required
                    disabled={loading || loadingAccounts || !!transaction}
                  >
                    <option value="">Select Account</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.identifier} - {account.paymentGateway}
                      </option>
                    ))}
                  </select>
                  {validationErrors.accountId && (
                    <span className="form-error">
                      {validationErrors.accountId}
                    </span>
                  )}
                  {loadingAccounts && (
                    <span className="form-info">Loading accounts...</span>
                  )}
                </div>

                <div className="setting-item">
                  <label htmlFor="terminalId">Terminal *</label>
                  <select
                    id="terminalId"
                    name="terminalId"
                    value={formData.terminalId}
                    onChange={handleInputChange}
                    className={`form-input ${
                      validationErrors.terminalId ? "form-input-error" : ""
                    }`}
                    required
                    disabled={loading || loadingAccounts || !formData.accountId || !!transaction}
                  >
                    <option value="">Select Terminal</option>
                    {availableTerminals.map((terminal) => (
                      <option key={terminal.id} value={terminal.id}>
                        {terminal.identifier} - {terminal.tid}
                      </option>
                    ))}
                  </select>
                  {validationErrors.terminalId && (
                    <span className="form-error">
                      {validationErrors.terminalId}
                    </span>
                  )}
                  {!formData.accountId && (
                    <span className="form-info">
                      Please select an account first
                    </span>
                  )}
                  {formData.accountId && availableTerminals.length === 0 && (
                    <span className="form-info">
                      No terminals available for this account
                    </span>
                  )}
                </div>

                <div className="setting-item">
                  <label htmlFor="orderId">Invoice *</label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <input
                      type="text"
                      id="orderId"
                      name="orderId"
                      value={formData.orderId}
                      onChange={handleInputChange}
                      className={`form-input ${
                        validationErrors.orderId ? "form-input-error" : ""
                      }`}
                      required
                      disabled={loading || !!transaction}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          orderId: generateOrderId(),
                        }))
                      }
                      className="wide-dash-border-btn"
                      disabled={loading || !!transaction}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      ↻
                    </button>
                  </div>
                  {validationErrors.orderId && (
                    <span className="form-error">{validationErrors.orderId}</span>
                  )}
                </div>

                <div className="setting-item">
                  <label htmlFor="amount">Amount *</label>
                  <input
                    type="text"
                    id="amount"
                    name="amount"
                    value={displayValues.amount}
                    onChange={handleCurrencyChange("amount")}
                    className={`form-input ${
                      validationErrors.amount ? "form-input-error" : ""
                    }`}
                    required
                    disabled={loading || !!transaction}
                    placeholder="$0.00"
                  />
                  {validationErrors.amount && (
                    <span className="form-error">{validationErrors.amount}</span>
                  )}
                </div>

                <div className="setting-item">
                  <label htmlFor="tipAmount">Tip Amount</label>
                  <input
                    type="text"
                    id="tipAmount"
                    name="tipAmount"
                    value={displayValues.tipAmount}
                    onChange={handleCurrencyChange("tipAmount")}
                    className={`form-input ${
                      validationErrors.tipAmount ? "form-input-error" : ""
                    }`}
                    disabled={loading || !!transaction}
                    placeholder="$0.00"
                  />
                  {validationErrors.tipAmount && (
                    <span className="form-error">
                      {validationErrors.tipAmount}
                    </span>
                  )}
                </div>
              </div>

              {error && (
                <div className="user-list-error" style={{ margin: "1rem 0" }}>
                  {error}
                </div>
              )}
            </form>
          ) : (
            <TransactionPreview
              formData={formData}
              displayValues={displayValues}
              accounts={accounts}
              availableTerminals={availableTerminals}
              error={error}
              onBack={handleBackToForm}
              onConfirm={handleConfirmTransaction}
              loading={loading}
            />
          )}
        </div>

        {!showPreview && (
          <div className="slide-panel-footer">
            <button
              type="button"
              className="btn secondary"
              onClick={handleClose}
              disabled={loading}
            >
              {transaction ? "Close" : "Cancel"}
            </button>
            {!transaction && (
              <button
                type="submit"
                className="btn primary"
                onClick={handlePreview}
                disabled={loading || loadingAccounts}
              >
                📋 Preview Transaction
              </button>
            )}
          </div>
        )}
        </div>
      )}
    </>
  );
};

export default AddTransaction;
